import React, { useCallback, useState } from 'react';

import { Grid } from '@lp/ds-next';
import { ColumnProps } from 'antd/lib/table';

import AddServiceLevelModal from '../components/AddServiceLevelModal';
import DuplicateServiceLevelModal from '../components/DuplicateServiceLevelModal';
import EditServiceLevelModal from '../components/EditServiceLevelModal';
import useDeleteServiceLevel from '../hooks/useDeleteServiceLevel';
import useGetServiceLevelsPaginated from '../hooks/useGetServiceLevelsPaginated';
import useServiceLevel from '../hooks/useServiceLevel';
import useServiceLevelsStore from '../hooks/useServiceLevelsStore';
import { ServiceLevel } from '../models/serviceLevel';
import PageTitle from '@/components/layout/PageTitle';
import StyledTable from '@/components/tables/StyledTable';
import { scopes } from '@/config';
import {
  getCurrentPage,
  getOffset,
  parseAntdSorter,
} from '@/helpers/antdTable';
import useAntdColumns from '@/hooks/useAntdColumns';

const ServiceLevelsList = () => {
  useGetServiceLevelsPaginated();
  const { result: resultDefault } = useServiceLevel(1);
  const deleteServiceLevel = useDeleteServiceLevel();
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [visibleClone, setVisibleClone] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>();

  const rowData = useServiceLevelsStore((state) => state.rowData);
  const loading = useServiceLevelsStore((state) => state.loading);
  const pageSize = useServiceLevelsStore((state) => state.pageSize);
  const offset = useServiceLevelsStore((state) => state.offset);
  const total = useServiceLevelsStore((state) => state.total);
  const dispatch = useServiceLevelsStore((state) => state.dispatch);

  const pagination = {
    current: getCurrentPage(pageSize, offset),
    pageSize,
    total,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} sur ${total} éléments`,
  };

  const handleArchive = useCallback(
    async (e, serviceLevel) => {
      await deleteServiceLevel(serviceLevel.id);
      // Refresh the table by dispatching a refresh action
      dispatch({ type: 'refresh' });
    },
    [deleteServiceLevel, dispatch]
  );

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      const [sortField, sortOrder] = parseAntdSorter(sorter);
      dispatch({
        type: 'updateSorting',
        args: { sortBy: sortField as any, order: sortOrder },
      });
      dispatch({
        type: 'updatePagination',
        args: {
          offset: getOffset(pagination.pageSize, pagination.current),
          pageSize: pagination.pageSize,
        },
      });
    },
    [dispatch]
  );

  const columns: ColumnProps<ServiceLevel>[] = useAntdColumns({
    columnsKeys: ['name'],
    addDefaultColumns: true,
    columnsSpecialProps: {
      createdAt: {
        sorter: false,
      },
      updatedAt: {
        sorter: false,
      },
    },
    deleteIconProps: {
      isPopover: true,
      onClick: handleArchive,
      requiredScopes: scopes.MANAGE_SERVICE_LEVELS,
    },
    eyeLinkProps: {
      onClick: (record) => {
        setSelectedRecord(record);
        setVisibleEdit(true);
      },
    },
    cloneIconProps: {
      requiredScopes: [scopes.MANAGE_SERVICE_LEVELS],
      onClick: (record) => {
        setSelectedRecord(record);
        setVisibleClone(true);
      },
    },
  });

  const fetchServiceLevels = useCallback(() => {
    dispatch({ type: 'refresh' });
    dispatch({ type: 'updatePagination', args: { pageSize, offset: 0 } });
  }, [dispatch, pageSize]);

  return (
    <Grid container justifyContent="center" spacing={3}>
      <Grid item mobile={11}>
        <PageTitle title="SERVICE LEVELS" />
      </Grid>

      <Grid item container mobile={11} justifyContent="flex-end">
        {resultDefault.state === 'success' && (
          <AddServiceLevelModal
            fetchServiceLevels={fetchServiceLevels}
            defaultConfig={resultDefault.resource}
          />
        )}
      </Grid>

      <Grid item mobile={11}>
        <StyledTable
          rowKey="id"
          dataSource={rowData.filter((item) => item.name !== 'Default offer')}
          columns={columns}
          size="small"
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Grid>
      {selectedRecord && visibleEdit && (
        <EditServiceLevelModal
          visible={visibleEdit}
          setVisible={setVisibleEdit}
          initialServiceLevel={selectedRecord}
          fetchServiceLevels={fetchServiceLevels}
        />
      )}
      {selectedRecord && visibleClone && (
        <DuplicateServiceLevelModal
          visible={visibleClone}
          setVisible={setVisibleClone}
          initialServiceLevel={selectedRecord}
          fetchServiceLevels={fetchServiceLevels}
        />
      )}
    </Grid>
  );
};

export default ServiceLevelsList;
